import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CellMetrics } from '../weekly-summary.component';
import { getExperienceColor, staffingColor, getQpiColor, getQpiGradient, QPI_COLOR_STOPS } from '../weekly-summary-colors';
import { MetricsTooltipService } from '../../../../../services/metrics-tooltip.service';

@Component({
  selector: 'app-slot-details',
  templateUrl: './slot-details.component.html',
  styleUrls: ['./slot-details.component.scss']
})
export class SlotDetailsComponent {
  @Input() slot: CellMetrics;
  @Input() visibleMetrics: any;
  @Input() showRawData: boolean = false;
  @Input() showCloseButton: boolean = false;
  @Output() close = new EventEmitter<void>();

  constructor(public tooltipService: MetricsTooltipService) {}

  getExperienceColor = getExperienceColor;
  staffingColor = staffingColor;
  getQpiColor = getQpiColor;
  getQpiGradient = getQpiGradient;
  qpiStops = QPI_COLOR_STOPS.filter(stop => stop.value !== 97).map(stop => ({ value: stop.value }));

  getBgColorSingle = (type: string, label: string, flag?: string) => {
    if (type === 'sales') {
      switch (label) {
        case 'Underperforming': return '#e57373';
        case 'Balanced': return '#81c784';
        case 'Overdelivering': return '#64b5f6';
        default: return '';
      }
    } else if (type === 'qpi') {
      switch (label) {
        case 'Green': return '#81c784';
        case 'Yellow': return '#fff176';
        case 'Orange': return '#ffb74d';
        case 'Red': return '#e57373';
        default: return '';
      }
    } else if (type === 'experience') {
      return this.getExperienceColor(label);
    } else if (type === 'staffing') {
      return this.staffingColor(label, flag);
    }
    return '';
  };

  getContrastTextColor = (backgroundColor: string) => {
    if (backgroundColor.startsWith('#')) {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6 ? 'rgba(0, 0, 0, 0.87)' : 'white';
    }
    return 'rgba(0, 0, 0, 0.87)';
  };

  formatQpiValue = (value: number) => (value * 100).toFixed(1) + '%';
  formatMoreSalesLabel = (option: string) => option ? option.charAt(0).toUpperCase() + option.slice(1) : '';
  getDuration = (cellData: CellMetrics) => cellData?.experience?.durations ? `${cellData.experience.durations.toFixed(1)} seconds` : 'Not available';
  getDurationRange = (cellData: CellMetrics, type?: string) => {
    let min = cellData.experience.durations_range.min;
    let max = cellData.experience.durations_range.max;
    if (type === 'estimate') {
      min = cellData.qpi.durations_estimate_low;
      max = cellData.qpi.durations_estimate_high;
    }
    if (min && max) {
      return `${min.toFixed(1)}-${max.toFixed(1)} seconds`;
    }
    return 'Not available';
  };
  getBottleneck = (cellData: CellMetrics) => cellData?.experience?.bottleneck ? `${cellData.experience.bottleneck.toFixed(1)} mins` : 'Not available';
  getBottleneckRange = (cellData: CellMetrics) => {
    const min = cellData.qpi.bottleneck_estimate_low;
    const max = cellData.qpi.bottleneck_estimate_high;
    if (min && max) {
      return `${min.toFixed(1)}-${max.toFixed(1)} mins`;
    }
    return 'Not available';
  };
  getLaborCost = (cellData: CellMetrics) => cellData?.staffing?.cost ? cellData.staffing.cost.toFixed(2) : 'Not available';
  getCoL = (cellData: CellMetrics) => cellData?.staffing?.col ? (cellData.staffing.col * 100).toFixed(1) : 'Not available';
  getCoLRange = (cellData: CellMetrics) => {
    const min = cellData.staffing.col_range.min;
    const max = cellData.staffing.col_range.max;
    if (min && max) {
      return `${(min * 100).toFixed(1)}-${(max * 100).toFixed(1)}%`;
    }
    return 'Not available';
  };
  getSalesDeviation = (sales: any) => {
    const value = sales.value;
    const min = sales.predicted.low;
    const max = sales.predicted.high;
    if (value < min) { return `-£${(min - value).toFixed(2)}`; }
    if (value > max) { return `+£${(value - max).toFixed(2)}`; }
    return 'Within range';
  };
  getSalesDeviationPercent = (sales: any) => {
    const deviation = this.getSalesDeviation(sales);
    if (deviation === 'Within range') return 'Within range';
    if (typeof sales.deviation_percent === 'number') {
      const absVal = Math.abs(sales.deviation_percent).toFixed(1);
      // Use the same sign as the currency deviation
      return (deviation.startsWith('+') ? '+' : '-') + absVal + '%';
    }
    return 'N/A';
  };
  getSalesPercentage = (sales: any) => {
    const value = sales.value;
    const min = sales.predicted.low;
    const max = sales.predicted.high;
    const range = max - min;
    if (range === 0) return 50;
    return Math.max(0, Math.min(100, ((value - min) / range) * 100));
  };


}
